/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link } from '@fluentui/react/lib/Link';
import { Separator } from '@fluentui/react/lib/Separator';
import { IStackItemProps, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { getEntityRoles } from 'src/luis/projects/entities/utils/entityHelpers';
import { BatchTestResultGraph } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/BatchTestResultGraph/BatchTestResultGraph';
import { BatchTestResultItemStats } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/BatchTestResultItemStats/BatchTestResultItemStats';
import { BatchTestResultUtteranceTable } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/BatchTestResultUtteranceTable/BatchTestResultUtteranceTable';
import { ModelList } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/ModelList/ModelList';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';
import { getModelConfusions } from 'src/luis/projects/testPane/utils/batchTestResultsCalculator';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { ConfusionType } from 'src/luis/types/stores/testPane/ConfusionType';

export type BatchBladeResultsProps = {
    /**
     * The batch test to view results for.
     */
    batchTest: BatchTest;

    /**
     * The intents in the application.
     */
    intents: Intent[];

    /**
     * The entities in the application.
     */
    entities: Entity[];

    /**
     * A callback to go back to the batch
     * test lists.
     */
    onGoBack: () => void;

    /**
     * Determines if the utterance text mode is right to left or not
     */
    isRTL: boolean;
};

const messages = defineMessages({
    batchTestTitle: {
        id: 'testPane.batchTestInspector.batchTestTitle',
        defaultMessage: "Dataset '{ batchTestName }' results"
    },
    backToList: {
        id: 'testPane.batchTestInspector.backToList',
        defaultMessage: 'Back to list'
    }
});

const { styleComponent } = getStylist('BatchBladeResults');

const ResultsArea = styleComponent<IStackProps>(Stack)('ResultsArea', theme => ({ height: theme.visualizationHeight }));
const ModelListContainer = styleComponent<IStackItemProps>(Stack.Item)('ModelListContainer', theme => ({ width: theme.modelListWidth }));

/**
 * Renders the batch test results details view, including the
 * graph, the model sidebar, and the selected utterances.
 */
export const BatchTestInspector = (props: BatchBladeResultsProps) => {
    const { batchTest, onGoBack, intents, entities, isRTL } = props;
    const [selectedModelId, setSelectedModelId] = React.useState<LuisId>(intents[0].id);
    const [selectedUtteranceIds, setSelectedUtteranceIds] = React.useState<LuisId[]>([]);
    const [withPrebuilts] = React.useState<boolean>(false);
    const { localize } = useLocalization();

    const modelConfusions = React.useMemo(() => getModelConfusions(batchTest, entities, withPrebuilts), [
        batchTest,
        entities,
        withPrebuilts
    ]);

    const selectedModel =
        intents.find(i => i.id === selectedModelId) ||
        entities.find(e => e.id === selectedModelId) ||
        getEntityRoles(entities).find(r => r.id === selectedModelId);

    const selectedModelConfusions = modelConfusions.get(selectedModelId);

    /**
     * Gets the utterance ids for the selected
     * confusion type.
     *
     * @param confusionType The confusion type to get the
     * utterances for.
     * @returns An array of utterance ids.
     */
    const getUtteranceIdsForConfusionType = (confusionType: ConfusionType) =>
        Array.from(selectedModelConfusions.scores.keys()).filter(id =>
            selectedModelConfusions.scores.get(id).some(s => s.confusion === confusionType)
        );

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Link onClick={onGoBack}>{localize(messages.backToList)}</Link>

            <LuisText variant="xLarge">{localize(messages.batchTestTitle, { batchTestName: batchTest.name })}</LuisText>

            <ResultsArea horizontal tokens={{ childrenGap: 15 }}>
                <Stack.Item styles={{ root: { flex: 1 } }}>
                    <BatchTestResultGraph
                        confusions={selectedModelConfusions}
                        onUtterancePointClick={id => setSelectedUtteranceIds([id])}
                        onConfusionClick={confusionType => setSelectedUtteranceIds(getUtteranceIdsForConfusionType(confusionType))}
                    />
                </Stack.Item>

                <ModelListContainer>
                    <ModelList
                        modelConfusions={modelConfusions}
                        selectedModelId={selectedModelId}
                        intents={intents}
                        entities={withPrebuilts ? entities : entities.filter(e => e.typeId !== ModelType.Prebuilt)}
                        onModelClicked={setSelectedModelId}
                    />
                </ModelListContainer>
            </ResultsArea>

            <Separator />

            {selectedModelId ? (
                <Stack tokens={{ childrenGap: 20 }}>
                    <BatchTestResultItemStats modelName={selectedModel && selectedModel.name} modelConfusions={selectedModelConfusions} />
                    <BatchTestResultUtteranceTable batchTest={batchTest} selectedUtteranceIds={selectedUtteranceIds} isRTL={isRTL} />
                </Stack>
            ) : null}
        </Stack>
    );
};
